import React, {
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
  useEffect,
  useRef,
  memo,
} from 'react';
import { IconChevronDown } from '../../icons';
import cn from '../../utils/cn';
import Button from '../buttons/button/Button';

/**
 * Features:
 * - Automatically detects if content needs expansion.
 * - Smooth height transitions when expanding/collapsing.
 * - Fade gradient effect on collapsed content (via CSS mask-image).
 * - ResizeObserver support to handle dynamic container size changes.
 *
 * WARNING: ResizeObserver is facultative, please use with care, it can be heavy on performance.
 */

type ExpandableContextValue = {
  isExpanded: boolean;
  expandable: boolean | null;
  toggleExpanded: () => void;
  contentRef: (node: HTMLDivElement | null) => void;
  collapsedHeight: number;
  transitionDuration: number;
};

const ExpandableContext = createContext<ExpandableContextValue | null>(null);

const useExpandableContext = () => {
  const context = useContext(ExpandableContext);

  if (!context) {
    throw new Error(
      'useExpandableContext must be used within an ExpandableContent component',
    );
  }

  return context;
};

type RootProps = {
  collapsedHeight?: number; // Maximum height (in px) when collapsed, content exceeding this will be truncated
  transitionDuration?: number; // Duration of the expand/collapse animation in milliseconds
  enableResizeObserver?: boolean; // Whether to observe container resize and recalculate dimensions automatically
} & React.ComponentPropsWithoutRef<'div'>;

const Root = memo(
  ({
    collapsedHeight = 200,
    transitionDuration = 300,
    enableResizeObserver = false,
    className,
    children,
    ...props
  }: RootProps) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [expandable, setExpandable] = useState<boolean | null>(null);

    const nodeRef = useRef<HTMLDivElement | null>(null);
    const hasInitialMeasure = useRef(false);

    // Measures the natural height of the content by temporarily removing
    // height and overflow constraints. This is synchronous so the browser
    // won't repaint between the removal and restoration — no visual flash.
    const measureContent = useCallback(
      (node: HTMLDivElement) => {
        const contentEl = node.querySelector<HTMLElement>('[data-slot="content"]');

        if (!contentEl) {
          return;
        }

        // Temporarily lift constraints to get the true content height
        const previousOverflow = contentEl.style.overflow;
        const previousHeight = node.style.height;
        contentEl.style.overflow = 'visible';
        node.style.height = 'auto';

        const naturalHeight = contentEl.scrollHeight;

        // Restore constraints immediately
        node.style.height = previousHeight;
        contentEl.style.overflow = previousOverflow;

        setExpandable(naturalHeight > collapsedHeight);
      },
      [collapsedHeight],
    );

    const contentRef = useCallback(
      (node: HTMLDivElement | null) => {
        // Callback refs don't persist the node reference automatically,
        // the ResizeObserver useEffect needs access to the node later.
        nodeRef.current = node;

        if (node) {
          measureContent(node);

          // Mark initial measure as done after the first render cycle
          // so the transition doesn't animate the initial collapse
          requestAnimationFrame(() => {
            hasInitialMeasure.current = true;
          });
        }
      },
      [measureContent],
    );

    // ResizeObserver setup for dynamic content/container size changes.
    // We observe the direct children of the content slot instead of the
    // root or the content slot itself, because when collapsed the root is
    // fixed at collapsedHeight and the content slot is overflow:hidden —
    // neither will report size changes when inner content shrinks.
    // The children, however, resize freely and trigger the observer.
    useEffect(() => {
      const node = nodeRef.current;

      if (!node || !enableResizeObserver) {
        return;
      }

      const contentEl = node.querySelector('[data-slot="content"]');

      if (!contentEl) {
        return;
      }

      const resizeObserver = new ResizeObserver(() => measureContent(node));

      Array.from(contentEl.children).forEach((child) => resizeObserver.observe(child));

      return () => {
        resizeObserver.disconnect();
      };
    }, [enableResizeObserver, measureContent]);

    const toggleExpanded = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const contextValue = useMemo<ExpandableContextValue>(
      () => ({
        isExpanded,
        expandable,
        toggleExpanded,
        contentRef,
        collapsedHeight,
        transitionDuration,
      }),
      [
        isExpanded,
        expandable,
        toggleExpanded,
        contentRef,
        collapsedHeight,
        transitionDuration,
      ],
    );

    return (
      <ExpandableContext.Provider value={contextValue}>
        <div
          ref={contentRef}
          className={cn('flex flex-col items-start gap-2 empty:hidden', className)}
          style={{
            interpolateSize: 'allow-keywords', // interpolateSize allows animating to 'auto'
            height:
              expandable === null
                ? `${collapsedHeight}px`
                : expandable && !isExpanded
                  ? `${collapsedHeight}px`
                  : 'auto',
            transitionProperty: expandable ? 'height' : 'none', // Only animate after initial measurement to prevent flash
            transitionDuration: `${transitionDuration}ms`,
          }}
          {...props}
        >
          {children}
        </div>
      </ExpandableContext.Provider>
    );
  },
);

Root.displayName = 'ExpandableContent.Root';

const Content = memo(
  ({ className, children, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
    const { expandable, isExpanded } = useExpandableContext();

    // Fade effect using CSS mask-image that works on any background
    const maskImage = useMemo(() => {
      if (!expandable || isExpanded) {
        return undefined;
      }

      return 'linear-gradient(to bottom, black calc(100% - 5rem), transparent 100%)';
    }, [expandable, isExpanded]);

    return (
      <div
        data-slot="content"
        className={cn('relative flex-1 overflow-hidden', className)}
        style={{
          maskImage,
          WebkitMaskImage: maskImage,
        }}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Content.displayName = 'ExpandableContent.Content';

// Button that only renders when content needs expansion
const Trigger = memo(({ ...props }: React.ComponentPropsWithoutRef<typeof Button>) => {
  const { isExpanded, expandable, toggleExpanded } = useExpandableContext();

  if (!expandable) {
    return null;
  }

  return (
    <Button onClick={toggleExpanded} {...props}>
      {isExpanded ? <span>Voir moins</span> : <span>Voir plus</span>}
      <IconChevronDown
        className={cn('size-2 transition-transform duration-300', {
          'rotate-180': isExpanded,
        })}
      />
    </Button>
  );
});

Trigger.displayName = 'ExpandableContent.Trigger';

const ExpandableContent = ({ children, ...props }: RootProps) => (
  <Root {...props}>
    <Content>{children}</Content>
    <Trigger />
  </Root>
);

const ExpandableContentCompound = Object.assign(ExpandableContent, {
  Root,
  Content,
  Trigger,
});

export default ExpandableContentCompound;
